import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { History, X } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime } from '@blimo/shared';
import {
  Badge,
  Button,
  DataTable,
  DescriptionList,
  EmptyState,
  ErrorState,
  Input,
  Label,
  PageHeader,
  SearchInput,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { AuditDiff, AuditSubject } from '../../components/audit.jsx';
import { formatAuditValue } from '../../components/audit-utils.js';
import { useListState } from '../../lib/list-search.js';

/**
 * Administration › Änderungsprotokoll (`audit-log.view`): wer hat wann was geändert. Filter in der URL,
 * Detail mit Vorher/Nachher im Sheet (`?entry=`). Sensible Werte entfernt bereits die API.
 */
export function AuditLogPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const filter = {
    subject_type: search.subject_type,
    subject_id: search.subject_id,
    causer_id: search.causer,
    log_name: search.log,
    event: search.event,
    from: search.from,
    to: search.to,
    q: search.q,
  };
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.auditLog(api, params));
  const options = useQuery(adminQueries.auditLogOptions(api));
  const opts = options.data;
  const navigate = useNavigate();
  /** Opens/closes the detail sheet without touching page or filters. @param {string|undefined} entry */
  const setEntry = (entry) => navigate({ to: '/administration/aenderungsprotokoll', search: (prev) => ({ ...prev, entry }), replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is stable
  const columns = useMemo(() => auditColumns(setEntry), [navigate]);

  return (
    <>
      <PageHeader
        title="Änderungsprotokoll"
        description="Wer hat wann was geändert – Anmeldungen, Stammdaten, Freigaben und Versand. Passwörter, Tokens und 2FA-Daten werden nie angezeigt."
      />
      <FilterBar>
        <SearchInput label="Protokoll durchsuchen" placeholder="Text, Wert, Name …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Objektart" value={search.subject_type} onChange={(v) => list.setFilter('subject_type', v)} options={opts?.subject_types ?? []} />
        <FilterSelect label="Bereich" value={search.log} onChange={(v) => list.setFilter('log', v)} options={opts?.log_names ?? []} />
        <FilterSelect
          label="Ereignis"
          value={search.event}
          onChange={(v) => list.setFilter('event', v)}
          options={(opts?.events ?? []).map((e) => ({ value: e, label: e }))}
        />
        <FilterSelect
          label="Ausgelöst von"
          value={search.causer}
          onChange={(v) => list.setFilter('causer', v)}
          options={[{ value: 'system', label: 'System (ohne Benutzer)' }, ...(opts?.causers ?? []).map((c) => ({ value: String(c.id), label: c.name }))]}
        />
        <DateFilter label="Von" value={search.from} onChange={(v) => list.setFilter('from', v)} />
        <DateFilter label="Bis" value={search.to} onChange={(v) => list.setFilter('to', v)} />
      </FilterBar>
      {search.subject_id && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Badge variant="info">
            Nur Objekt #{search.subject_id}
            {search.subject_type ? ` (${opts?.subject_types?.find((t) => t.value === search.subject_type)?.label ?? search.subject_type})` : ''}
          </Badge>
          <Button size="sm" variant="ghost" onClick={() => list.setFilter('subject_id', undefined)}>
            <X aria-hidden="true" /> Objektfilter entfernen
          </Button>
        </div>
      )}
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Änderungsprotokoll"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          onRowClick={(row) => setEntry(String(row.id))}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={History}
              title="Keine Einträge"
              description="Für die gewählten Filter gibt es keine Protokolleinträge."
            />
          }
        />
      )}
      <EntrySheet id={search.entry} onClose={() => setEntry(undefined)} />
    </>
  );
}

/** @param {{ label: string, value?: string, onChange: (v: string|undefined) => void }} props */
function DateFilter({ label, value, onChange }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input id={id} type="date" className="h-8 sm:w-40" value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />
    </div>
  );
}

/** @param {(entry: string) => void} openEntry */
function auditColumns(openEntry) {
  return [
    {
      id: 'created_at',
      header: 'Zeitpunkt',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric whitespace-nowrap">{formatDateTime(row.original.created_at)}</span>,
    },
    {
      id: 'event',
      header: 'Ereignis',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.event_label}</span>
          {row.original.log_label !== row.original.event_label && <span className="text-xs text-muted-foreground">{row.original.log_label}</span>}
        </span>
      ),
    },
    { id: 'subject', header: 'Objekt', enableSorting: false, cell: ({ row }) => <AuditSubject subject={row.original.subject} /> },
    {
      id: 'causer',
      header: 'Ausgelöst von',
      enableSorting: false,
      cell: ({ row }) => row.original.causer?.name ?? <span className="text-muted-foreground">System</span>,
    },
    {
      id: 'changes',
      header: 'Geänderte Felder',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.changed_attributes?.length ? (
          <span className="text-xs">{row.original.changed_attributes.join(', ')}</span>
        ) : (
          <span className="text-xs text-muted-foreground">{row.original.event_label === row.original.description ? '–' : row.original.description}</span>
        ),
    },
    {
      id: 'details',
      header: () => <span className="sr-only">Details</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Details zu Eintrag #${row.original.id}`}
          onClick={(e) => {
            e.stopPropagation();
            openEntry(String(row.original.id));
          }}
        >
          Details
        </Button>
      ),
    },
  ];
}

/** Detail of one entry (old/new diff, additional properties). @param {{ id?: string, onClose: () => void }} props */
function EntrySheet({ id, onClose }) {
  const api = useApi();
  const entry = useQuery({ ...adminQueries.auditLogEntry(api, id ?? '0'), enabled: Boolean(id) });
  const data = entry.data;
  return (
    <Sheet open={Boolean(id)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{data ? data.event_label : 'Protokolleintrag'}</SheetTitle>
          <SheetDescription>{data ? `${data.log_label} · ${formatDateTime(data.created_at)}` : `Eintrag #${id ?? ''}`}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 pb-4">
          {entry.error ? (
            <ErrorState compact error={entry.error} onRetry={() => entry.refetch()} />
          ) : !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <DescriptionList
                items={[
                  { label: 'Objekt', value: data.subject ? <AuditSubject subject={data.subject} /> : null },
                  { label: 'Ausgelöst von', value: data.causer?.name ?? 'System' },
                  { label: 'Beschreibung', value: data.description },
                  { label: 'Ereignis', value: data.event ? <code className="text-xs">{data.event}</code> : null },
                  { label: 'Eintrag', value: `#${data.id}` },
                ]}
              />
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Änderungen</h3>
                <AuditDiff changes={data.changes} />
              </section>
              {data.properties && Object.keys(data.properties).length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">Weitere Angaben</h3>
                  <DescriptionList
                    labelWidth="9rem"
                    items={Object.entries(data.properties).map(([key, value]) => ({ key, label: key, value: formatAuditValue(value) }))}
                  />
                </section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
